"use client";

import {useRef, useState, useEffect, useMemo} from "react";
import {useClickAway} from "react-use";
import Link from "next/link";
import _ from "lodash";

import {$core} from '@isudio/core';
import {cn, useRxSync} from "@isudio/utils";
import {$store, $socket, type MenuItem} from '@isudio/globals';
import {type IDevicePayload, UserPresence} from "@isudio/data";
import {useOpenComponent} from "../";

// noinspection JSUnusedGlobalSymbols
const actions: any = {
  forgetAccount: () => {
    _.forgetAccount();
  },
  signout: async () => {
    const payload: IDevicePayload = _.omitBy({
      ...$store.Device.data || null,
      ...{deviceTime: new Date().toISOString()}
  }, _.isNil) as IDevicePayload;

    await $core.signout(payload).then((_data) => {
      if ($socket.socketClient) $socket.socketClient.disconnect();
      _.get($store, 'User.session$').next(null);
      _.toastSuccess('Signed out from your acount successfully.');

    }).catch((e) => {
      const t = setTimeout(async () => {
        _.toastError(_.get(e, 'message'));
        clearTimeout(t);
      }, 10);
    })
  },
  changePresence: async (presence: UserPresence) => {
    await $socket.send('client:event', {
      action: 'client:user:presence:changed',
      data: {
        ..._.pick($store.User.session, ['uid', 'did']),
        presence
      }
    });
  }
}

const isVisible = (item: any) => {
  if ('visible' in item) {
    return typeof item.visible === "function" ? item.visible() : item.visible;
  } else {
    return false;
  }
};

function useVisibleMenuItems(): MenuItem[] {
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const sub = $store.App.menuRefresh$?.subscribe((tick: number) => {
      // Trigger re-render when refresh is requested
      setRefreshTick(tick);
    });
    return () => sub?.unsubscribe();
  }, []);

  const rawMenu: any = _.cloneDeep($store.App.menuItems$$);
  return useMemo(() => {
    return normalizeMenu(rawMenu)
      .filter((item) => isVisible(item))
      .map((item) => {
        if (!item.submenu && !item.links) return item;

        if (item.submenu && Array.isArray(item.submenu)) {
          item.submenu = item.submenu.filter((sub) => isVisible(sub))
        }

        if (item.links && Array.isArray(item.links)) {
          item.links = item.links.filter((link) => isVisible(link))
        }

        return item;
      });
  }, [refreshTick]) as MenuItem[];
}

function normalizeMenu(json: any[]): MenuItem[] {
  return json.map((item) => {
    if (item.seperator === "line" && item.visible) return { seperator: "line" };
    return item;
  });
}

const resolveUser = (userFn?: () => any) => {
  try {
    const user = typeof userFn === "function" ? userFn() : null;
    return user ?? null;
  } catch {
    return null;
  }
};

export const Menu = () => {
  const menu = useVisibleMenuItems();
  const clickPos: any = useRxSync($store.App.mousePosition$);
  const isOpen: any = useRxSync($store.App.showMenu$);

  const ref = useRef(null);
  const [canClose, setCanClose] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const openComponent = useOpenComponent();

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isOpen) {
      timeout = setTimeout(() => setCanClose(true), 1000); // adjust if needed
    } else {
      setExpanded(null);
      setCanClose(false);
    }
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useClickAway(ref, () => {
    if (isOpen && canClose) {
      requestAnimationFrame(() => {
        $store.App.showMenu$.next(false);
        setExpanded(null);
      });
    }
  });

  const editProfile = () => {
    if (!_.isUserSignedIn()) return;
    openComponent("userProfile");
  }

  return (
    <div
      ref={ref}
      className="fixed h-0 w-0 z-50"
      onWheel={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onTouchMove={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
        {isOpen && (
          <div
            key="menu-popover"
            style={{
              position: "absolute",
              left: clickPos.x - 290, //left: clickPos.x - 250,
              top: clickPos.y, //top: clickPos.y + 40,
              transformOrigin: "top right",
            }}
            className="bg-custom-blue-light/70 border border-none rounded-md shadow-2xl p-2 w-72 z-9999 menu-scroll"
          >
            <ul
              className="flex flex-col gap-1">
              {menu.map((item, idx) => {
                if ("seperator" in item) {
                  return (
                    <li
                      key={`separator-${idx}`}
                      className="border-t border-none my-1"/>
                  );
                }

                const hasSubmenu = Array.isArray(item.submenu);
                const isExpanded = expanded === item.title;

                return (
                  <li
                    key={item.title}
                    className="rounded-md bg-custom-blue-darker/80 overflow-hidden">
                    <button
                      onClick={() => {
                        if (hasSubmenu) {
                          setExpanded((prev) => (prev === item.title ? null : item.title));

                        } else {
                          $store.App.showMenu$.next(false);
                          if (item.component) {
                            openComponent(item.id);
                          }
                        }
                      }}
                      className={`group flex items-center justify-between w-full px-4 py-3 relative ${item?.links ? '' : 'cursor-pointer'} transition-colors`}
                    >
                      {typeof item.user === "function" ? (() => {
                        const user = resolveUser(item.user);
                        if (!user) return null;

                        return (
                          <div className="flex items-center w-full">
                            {/* Column 1 - Thumbnail */}
                            <div
                              className={cn(
                                "shrink-0 w-fit h-fit flex items-center justify-center cursor-default",
                                _.isUserSignedIn() ? "cursor-pointer" : "cursor-default"
                              )}
                              onClick={() => editProfile()}>
                              <img
                                src={user.photo ?? "/images/default-profile.png"}
                                alt={`${user.firstName} ${user.lastName}`}
                                className="w-13 h-13 rounded-full object-cover border-none shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]"
                              />
                            </div>

                            {/* Column 2 - Name + Link */}
                            <div className="flex flex-col flex-1 pl-3 text-left">
                              <span
                                className={cn(
                                "max-w-40 truncate text-white text-sm font-semibold leading-tight cursor-default transition duration-200",
                                  _.isUserSignedIn() ? "hover:text-generic-hover hover:scale-105 cursor-pointer" : "text-white cursor-default"
                                )}
                                onClick={() => editProfile()}>
                                {user.firstName} {user.lastName}
                              </span>
                              {item.links && (
                                <div className="flex flex-row gap-4">
                                  {item.links
                                    .map((link) => (
                                      <button
                                        key={link.title}
                                        className="mt-1 text-xs underline cursor-pointer transition duration-200 text-bluegray-lighter hover:text-generic-hover hover:scale-105"
                                        onClick={() => {
                                          $store.App.showMenu$.next(false);
                                          if (link.component) openComponent(link.id);
                                          if (link.action) actions[link.action](link);
                                        }}
                                      >
                                        {link.title}
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })() : (
                        // Default layout (icon + title)
                        <span className="flex items-center gap-2 text-sm transition duration-200 text-white group-hover:text-generic-hover group-hover:scale-105">
                          <i className={`fa-duotone fa-light ${item.icon} text-2xl w-8`} />
                            {item.title}
                        </span>
                      )}

                      {hasSubmenu ? (
                        <i
                          className={`fa-duotone fa-light fa-chevron-down text-xs transition duration-200 text-white group-hover:text-generic-hover group-hover:scale-105 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      ) : (
                        (item.route) && (
                          <Link
                            href={item.route as string}
                            className="absolute inset-0"
                            onClick={() => {
                              $store.App.showMenu$.next(false);
                              setExpanded(null);
                            }}
                          />
                        )
                      )}
                    </button>

                      {hasSubmenu && isExpanded && (
                        <ul
                          className="bg-transparent"
                        >
                          {Array.isArray(item.submenu) && item.submenu.map((sub) => (
                            <li
                              key={sub.title}
                            >
                              {sub.route ? (
                                <Link
                                  href={sub.route}
                                  onClick={() => {
                                    $store.App.showMenu$.next(false);
                                    setExpanded(null);
                                  }}
                                  className="flex items-center gap-2 px-10 py-2 text-sm transition duration-200 text-white hover:text-generic-hover hover:scale-105"
                                >
                                  <i
                                    className={`fa-duotone fa-light ${sub.icon} text-2xl w-8`}
                                  />
                                  {sub.title}
                                </Link>
                              ) : (
                                <button
                                  onClick={() => {
                                    $store.App.showMenu$.next(false);
                                    setExpanded(null);

                                    if (sub.component) {
                                      openComponent(sub.id);
                                    }
                                  }}
                                  className="flex items-center gap-2 px-10 py-2 text-sm transition duration-200 text-white hover:text-generic-hover hover:scale-105 w-full text-left cursor-pointer"
                                >
                                  <i className={`fa-duotone fa-light ${sub.icon} text-2xl w-8`} />
                                  {sub.title}
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
    </div>
  );
};
